import { captureException } from "@sentry/react";
import axios from "axios";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { X } from "react-feather";
import { Tooltip } from "react-tooltip5";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { workspaceApiEndpoints, getErrorMessage } from "../utils/constants";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { getUserActivePlan } from "@helpers/profileHelper";
import { getSidebarCountData } from "@redux/Sidebar/sidebarCount.action";

type Company = {
  company_name?: string;
  key?: string;
  hashed_id?: string;
  company_id?: string;
  comp_id?: string;
  address?: string;
  company_address?: string;
  address_line?: string;
  country?: string;
  company_country?: string;
  country_name?: string;
};

type ProspectType = "buyer" | "supplier";

type WorkspaceItem = {
  id?: string | number;
  name?: string;
  prospect_type?: ProspectType | null;
  companies_count?: number;
  company_count?: number;
  updated_at?: string;
  modified_at?: string;
  created_at?: string;
};

type AddCompaniesToWorkspaceModalProps = {
  show: boolean;
  onHide?: () => void;
  companies?: Company[];
  onSuccess?: () => void;
  type?: string;
  /** Let the user pick Buyer/Supplier Prospect in the modal instead of using `type`. */
  selectProspectType?: boolean;
  /** Prospect types the company has trade data for; others are disabled. Omit to enable both. */
  availableProspectTypes?: ProspectType[];
  workspaceCreationFilters?: Record<string, unknown> | null;
  source?: string;
  /** Source reported to analytics when it differs from the one sent to the API. */
  trackingSource?: string;
  searchApiEndpoint?: string;
  /** The user's questions, oldest first, from which the backend extracts the new list's search phrase (Hermes). */
  searchQueries?: string[];
};

const getWorkspaceId = (workspace?: WorkspaceItem) => workspace?.id;
const getCompanyLabel = (company: Company) => (company?.company_name ? company.company_name : company?.key || "-");
const getCompanyId = (company: Company) => company?.hashed_id || company?.company_id || company?.comp_id;
const getCompanyAddress = (company: Company) => company?.address || company?.company_address || company?.address_line || "";
const getCompanyCountry = (company: Company) => company?.country || company?.company_country || company?.country_name || "";
const getWorkspaceCompanyCount = (workspace: WorkspaceItem) => workspace?.companies_count || workspace?.company_count || 0;
const getWorkspaceUpdatedAt = (workspace: WorkspaceItem) => workspace?.updated_at || workspace?.modified_at || workspace?.created_at;
const toProspectType = (value?: string): ProspectType | "" => (value === "buyer" || value === "supplier" ? value : "");

const PROSPECT_TYPE_OPTIONS: { value: ProspectType; label: string; unavailableHint: string }[] = [
  { value: "buyer", label: "Buyer", unavailableHint: "No import shipments found for this company" },
  { value: "supplier", label: "Supplier", unavailableHint: "No export shipments found for this company" },
];

const AddCompaniesToWorkspaceModal = ({ show, onHide, companies = [], onSuccess, type, selectProspectType = false, availableProspectTypes, workspaceCreationFilters = null, source = "", trackingSource, searchApiEndpoint = "", searchQueries }: AddCompaniesToWorkspaceModalProps) => {
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const [workspaceList, setWorkspaceList] = useState<WorkspaceItem[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | number>("");
  const [activeTab, setActiveTab] = useState<"existing" | "create">("create");
  const [searchWorkspaceTerm, setSearchWorkspaceTerm] = useState("");
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [companiesToAdd, setCompaniesToAdd] = useState<Company[]>([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProspectType, setSelectedProspectType] = useState<ProspectType | "">("");
  const newWorkspaceInputRef = useRef<HTMLInputElement>(null);
  const prospectType = selectProspectType ? selectedProspectType : toProspectType(type);
  const isProspectTypeMissing = selectProspectType && !prospectType;

  useEffect(() => {
    if (activeTab === "create") {
      newWorkspaceInputRef.current?.focus();
    }
  }, [activeTab]);

  const normalizedCompanies = useMemo(() => (Array.isArray(companies) ? companies : []), [companies]);
  const filteredWorkspaceList = useMemo(() => {
    if (!searchWorkspaceTerm.trim()) return workspaceList;
    const normalizedTerm = searchWorkspaceTerm.trim().toLowerCase();
    return workspaceList.filter((workspace) =>
      String(workspace?.name || "")
        .toLowerCase()
        .includes(normalizedTerm)
    );
  }, [workspaceList, searchWorkspaceTerm]);

  useEffect(() => {
    if (!show) return;
    setSearchWorkspaceTerm("");
    setNewWorkspaceName("");
    setSelectedProspectType("");
    setActiveTab("create");
  }, [show]);

  // Existing lists are filtered to the chosen prospect type. Before one is picked, all lists are
  // fetched so the tab count shows the total; the list itself stays hidden until a type is chosen.
  useEffect(() => {
    if (!show) return;
    setSelectedWorkspaceId("");
    let cancelled = false;
    const fetchWorkspaceList = async () => {
      setIsLoadingWorkspaces(true);
      try {
        const { data } = await axios.get(workspaceApiEndpoints.getIntelWorkspaces, { params: prospectType ? { prospect_type: prospectType } : {} });
        if (!cancelled) setWorkspaceList(data?.workspaces || []);
      } catch (error) {
        if (cancelled) return;
        captureException(error);
        setWorkspaceList([]);
        toast.error("Unable to fetch workspaces");
      } finally {
        if (!cancelled) setIsLoadingWorkspaces(false);
      }
    };
    fetchWorkspaceList();
    return () => {
      cancelled = true;
    };
  }, [show, selectProspectType, prospectType]);

  useEffect(() => {
    if (!show) return;
    setCompaniesToAdd(normalizedCompanies);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleCreateWorkspace = async (): Promise<string | number> => {
    const workspaceName = newWorkspaceName.trim();
    if (!workspaceName) return "";
    setIsCreatingWorkspace(true);
    try {
      const createWorkspacePayload: { name: string; filters?: Record<string, unknown>; source?: string; search_api_endpoint?: string; prospect_type?: ProspectType; search_queries?: string[] } = { name: workspaceName };
      if (prospectType) {
        createWorkspacePayload.prospect_type = prospectType;
      }
      if (workspaceCreationFilters) {
        createWorkspacePayload.filters = workspaceCreationFilters;
      }
      if (source) {
        createWorkspacePayload.source = source;
      }
      if (searchApiEndpoint) {
        createWorkspacePayload.search_api_endpoint = searchApiEndpoint;
      }
      if (searchQueries?.length) {
        createWorkspacePayload.search_queries = searchQueries;
      }
      const { data } = await axios.post(workspaceApiEndpoints.createIntelWorkspace, createWorkspacePayload);
      const createdWorkspace: WorkspaceItem = data?.workspace || data?.data || data;
      const createdWorkspaceId = getWorkspaceId(createdWorkspace);
      if (createdWorkspaceId) {
        setWorkspaceList((prev) => [createdWorkspace, ...prev]);
        setSelectedWorkspaceId(createdWorkspaceId);
        setNewWorkspaceName("");
        return createdWorkspaceId;
      }
      return "";
    } catch (error) {
      captureException(error);
      toast.error(getErrorMessage(error, "Unable to create workspace"));
      return "";
    } finally {
      setIsCreatingWorkspace(false);
    }
  };

  const getRelativeTimeLabel = (dateValue?: string) => {
    if (!dateValue) return "Updated recently";
    const timestamp = new Date(dateValue).getTime();
    if (Number.isNaN(timestamp)) return "Updated recently";
    const differenceInMs = Date.now() - timestamp;
    const dayInMs = 24 * 60 * 60 * 1000;
    const diffDays = Math.max(1, Math.floor(differenceInMs / dayInMs));
    if (diffDays === 1) return "Updated yesterday";
    if (diffDays < 7) return `Updated ${diffDays} days ago`;
    if (diffDays < 14) return "Updated one week ago";
    return "Updated recently";
  };

  const handleAddCompanies = async () => {
    if (companiesToAdd.length === 0) return;
    setIsSubmitting(true);
    try {
      let targetWorkspaceId: string | number = selectedWorkspaceId;
      if (activeTab === "create") {
        const createdWorkspaceId = await handleCreateWorkspace();
        if (!createdWorkspaceId) return;
        targetWorkspaceId = createdWorkspaceId;
      }
      if (!targetWorkspaceId) return;
      const payloadCompanies = companiesToAdd
        .map((company) => ({
          company_id: getCompanyId(company),
          company_name: getCompanyLabel(company),
          address: getCompanyAddress(company),
          country: getCompanyCountry(company),
          company_type: prospectType || type,
        }))
        .filter((company) => Boolean(company.company_id));
      const payload: { companies: typeof payloadCompanies; source?: string } = { companies: payloadCompanies };
      if (source) {
        payload.source = source;
      }
      const { data } = await axios.post(workspaceApiEndpoints.addIntelWorkspaceCompanies(targetWorkspaceId), payload);
      const addedCount: number = data?.added_count ?? payloadCompanies.length;
      const skippedCount: number = data?.skipped_count ?? 0;
      toast.success(
        <span>
          {addedCount} {addedCount === 1 ? "company" : "companies"} added to workspace.
          {skippedCount > 0 ? ` ${skippedCount} already in this list.` : ""} <br />
          <button
            type="button"
            className="tw-bg-transparent tw-border-0 tw-p-0 !tw-text-inherit !tw-font-semibold !tw-underline tw-cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              history.push("/workspace", { workspaceId: targetWorkspaceId });
              toast.dismiss();
            }}>
            View workspace
          </button>
        </span>,
        { closeOnClick: false }
      );
      trademoTrackingEvents(trackingEventDictionary.workspaceAddCompanies, {
        ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()),
        source: trackingSource || source,
        prospectType,
        companiesCount: payloadCompanies.length,
      });
      dispatch(getSidebarCountData());
      onHide?.();
      onSuccess?.();
    } catch (error) {
      captureException(error);
      toast.error(getErrorMessage(error, "Unable to add companies to workspace"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeCompanyFromAddList = (companyToRemove: Company, indexToRemove: number) => {
    setCompaniesToAdd((prev) => prev.filter((item, index) => (getCompanyId(companyToRemove) ? getCompanyId(item) !== getCompanyId(companyToRemove) : index !== indexToRemove)));
  };

  return (
    <Modal centered size="xl" show={show} onHide={onHide} dialogClassName="tw-max-w-[980px]">
      <Modal.Header closeButton className="tw-border-b tw-border-gray-200 tw-px-6 tw-py-4">
        <Modal.Title className="tw-text-2xl tw-font-semibold tw-text-gray-900">Add Companies to workspace</Modal.Title>
      </Modal.Header>
      <Modal.Body className="tw-p-0">
        <div className="tw-grid tw-grid-cols-2 tw-min-h-[420px]">
          <div className="tw-px-6 tw-py-5 tw-max-h-[520px] tw-overflow-y-auto">
            <p className="tw-text-base tw-font-medium tw-text-gray-900 tw-mb-3">Adding {companiesToAdd.length} companies</p>
            <div className="tw-space-y-2.5">
              {companiesToAdd.map((company, index) => (
                <div key={`${getCompanyId(company) || getCompanyLabel(company)}`} className="tw-flex tw-items-center tw-justify-between tw-w-full tw-max-w-[380px] tw-border tw-border-gray-200 tw-rounded-full tw-px-3 tw-py-1.5">
                  <span className="tw-text-sm tw-text-gray-900 tw-pr-2 tw-truncate">{getCompanyLabel(company)}</span>
                  <button className="tw-border-0 tw-bg-transparent tw-p-0 tw-leading-none" onClick={() => removeCompanyFromAddList(company, index)}>
                    <X size={13} className="tw-text-gray-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="tw-px-6 tw-py-5 tw-border-l tw-border-gray-200 tw-max-h-[520px] tw-overflow-y-auto">
            {selectProspectType ? (
              <div className="tw-mb-5">
                <p className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-3">Add as</p>
                <div className="tw-flex tw-gap-3">
                  {PROSPECT_TYPE_OPTIONS.map((option) => {
                    const isSelected = selectedProspectType === option.value;
                    const isDisabled = availableProspectTypes?.length ? !availableProspectTypes.includes(option.value) : false;
                    return (
                      <button key={option.value} type="button" disabled={isDisabled} title={isDisabled ? option.unavailableHint : undefined} className={`tw-flex-1 tw-rounded-lg tw-border tw-px-3 tw-py-2.5 tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-gray-900 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed ${isSelected ? "tw-border-primary-purple-500 tw-bg-primary-purple-50" : "tw-border-gray-200 tw-bg-white"}`} onClick={() => setSelectedProspectType(option.value)}>
                        <span className={`tw-h-4 tw-w-4 tw-rounded-full tw-border tw-flex tw-items-center tw-justify-center ${isSelected ? "tw-border-primary-purple-500" : "tw-border-gray-300"}`}>{isSelected ? <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-purple-500" /> : null}</span>
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <p className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-4">Add to</p>
            <div className="tw-flex tw-items-end tw-gap-6 tw-border-b tw-border-gray-200 tw-mb-3">
              <button className={`tw-pb-2 tw-text-sm tw-flex tw-items-center tw-gap-1 !tw-bg-transparent ${activeTab === "create" ? "tw-text-gray-900 tw-border-b-2 tw-border-primary-purple-500" : "tw-text-gray-500"}`} onClick={() => setActiveTab("create")}>
                Create new list{" "}
                <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="#101828">
                  <path d="M450-140v-310H140v-60h310v-310h60v310h310v60H510v310h-60Z" />
                </svg>
              </button>
              <button className={`tw-pb-2 tw-text-sm !tw-bg-transparent ${activeTab === "existing" ? "tw-text-gray-900 tw-border-b-2 tw-border-primary-purple-500" : "tw-text-gray-500"}`} onClick={() => setActiveTab("existing")}>
                Existing List <span className="tw-text-xs tw-bg-primary-purple-50 tw-text-primary-purple-600 tw-px-1.5 tw-py-0.5 tw-rounded-md">{workspaceList.length}</span>
              </button>
            </div>

            {activeTab === "create" ? (
              <div>
                <p className="tw-text-sm tw-text-gray-700 tw-mb-2">Enter the name of the list</p>
                <input ref={newWorkspaceInputRef} className="tw-w-full tw-h-10 tw-rounded-md tw-border tw-border-gray-200 tw-px-3 tw-text-sm tw-outline-none focus:tw-border-primary-purple-500" value={newWorkspaceName} onChange={(event) => setNewWorkspaceName(event.target.value)} placeholder="ex: high value prospects" />
              </div>
            ) : (
              <div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-h-10 tw-rounded-md tw-border tw-border-gray-200 tw-px-3 tw-mb-3 focus-within:tw-border-primary-purple-500">
                  <span className="material-symbols-outlined tw-text-base tw-text-gray-400 tw-leading-none">search</span>
                  <input className="tw-w-full tw-h-full tw-border-none tw-p-0 tw-text-sm tw-outline-none" value={searchWorkspaceTerm} onChange={(event) => setSearchWorkspaceTerm(event.target.value)} placeholder="Search list" />
                </div>
                <div className="tw-space-y-2">
                  {selectProspectType && !prospectType ? (
                    <div className="tw-text-sm tw-text-gray-500">Select buyer or supplier to see your lists</div>
                  ) : isLoadingWorkspaces ? (
                    <div className="tw-text-sm tw-text-gray-500">Loading workspaces...</div>
                  ) : filteredWorkspaceList.length === 0 ? (
                    <div className="tw-text-sm tw-text-gray-500">No workspaces found</div>
                  ) : (
                    filteredWorkspaceList.map((workspace) => {
                      const workspaceId = getWorkspaceId(workspace);
                      const isSelected = selectedWorkspaceId === workspaceId;
                      return (
                        <button key={workspaceId} className={`tw-w-full tw-text-left tw-rounded-lg tw-border tw-px-3 tw-py-2.5 tw-flex tw-items-start tw-gap-3 ${isSelected ? "tw-border-primary-purple-500 tw-bg-primary-purple-50" : "tw-border-gray-200 tw-bg-white"}`} onClick={() => setSelectedWorkspaceId(workspaceId ?? "")}>
                          <span className={`tw-mt-1 tw-h-4 tw-w-4 tw-rounded-full tw-border tw-flex tw-items-center tw-justify-center ${isSelected ? "tw-border-primary-purple-500" : "tw-border-gray-300"}`}>{isSelected ? <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-purple-500" /> : null}</span>
                          <span className="tw-flex tw-flex-col tw-gap-0.5">
                            <span className="tw-text-sm tw-text-gray-900">{workspace?.name || "Workspace"}</span>
                            <span className="tw-text-xs tw-text-gray-500">
                              {getWorkspaceCompanyCount(workspace)} companies · {getRelativeTimeLabel(getWorkspaceUpdatedAt(workspace))}
                            </span>
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="tw-border-t tw-border-gray-200 tw-px-6 tw-py-3">
        <button className="tw-text-sm tw-font-medium tw-text-gray-900 tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-w-fit" onClick={onHide}>
          Cancel
        </button>
        {/* Disabled buttons fire no hover events, so the tooltip anchors on a wrapper. */}
        <span className="tw-inline-flex" data-tooltip-id="add-to-workspace-submit-tooltip" data-tooltip-content={isProspectTypeMissing ? "Select one: Buyer (Import) or Supplier (Export)" : undefined}>
          <button className="tw-text-sm tw-font-medium tw-text-white tw-bg-primary-purple-500 tw-rounded-md tw-px-3 tw-py-2 tw-w-fit disabled:tw-opacity-50 disabled:tw-cursor-not-allowed" onClick={handleAddCompanies} disabled={isProspectTypeMissing || (activeTab === "existing" && !selectedWorkspaceId) || (activeTab === "create" && !newWorkspaceName.trim()) || companiesToAdd.length === 0 || isSubmitting || isCreatingWorkspace}>
            {isSubmitting ? "Adding..." : "Add to Workspace"}
          </button>
        </span>
        <Tooltip id="add-to-workspace-submit-tooltip" place="top" className="tw-z-[1100] !tw-opacity-100" />
      </Modal.Footer>
    </Modal>
  );
};

export default memo(AddCompaniesToWorkspaceModal);
